import { memo, useEffect, useRef, useState } from 'react';
import { formatCredits, microToCredits } from '@kumio/shared';
import { AppIcon, Mark } from '../brand/brand';
import { tokenLabel, useCoordinator } from '../state/coordinator';
import { shortAddr, useWallet } from '../state/wallet';
import { APPS, START_ORDER, type AppId } from './apps';
import { useProcesses } from './processes';
import { openApp, useWm } from './wm';

function Clock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 5000);
    return () => clearInterval(t);
  }, []);
  return (
    <time className="clock" dateTime={now.toISOString()} title={now.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}>
      {now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
    </time>
  );
}

const TaskButtons = memo(function TaskButtons() {
  const order = useWm((s) => s.order);
  const focused = useWm((s) => s.focused);
  const mins = useWm((s) => order.map((id) => (s.wins[id]?.minimized ? '1' : '0')).join(''));
  const busy = useProcesses((s) => Object.values(s.procs).filter((p) => p.active).map((p) => p.app).join(','));
  return (
    <div className="tasks" role="toolbar" aria-label="Open windows">
      {order.map((id, i) => {
        const min = mins[i] === '1';
        const active = focused === id && !min;
        return (
          <button
            key={id}
            data-task={id}
            className={'task' + (active ? ' active' : '') + (min ? ' minimized' : '')}
            aria-pressed={active}
            title={APPS[id].title + (min ? ' (minimized)' : '')}
            onClick={() => useWm.getState().taskbarToggle(id)}
          >
            <AppIcon name={APPS[id].icon} size={20} />
            <span>{APPS[id].title}</span>
            {busy.split(',').includes(id) && <i className="task-busy" aria-label="running" />}
          </button>
        );
      })}
    </div>
  );
});

function fmtRate(h: number) {
  const u = ['H/s', 'kH/s', 'MH/s', 'GH/s', 'TH/s'];
  let i = 0;
  while (h >= 1000 && i < u.length - 1) {
    h /= 1000;
    i++;
  }
  return h.toFixed(h < 10 ? 2 : h < 100 ? 1 : 0) + ' ' + u[i];
}
export { fmtRate };

const Tray = memo(function Tray() {
  const procs = useProcesses((s) => s.procs);
  const coord = useCoordinator((s) => s.status);
  const config = useCoordinator((s) => s.config);
  const balance = useWallet((s) => s.balance);
  const balStatus = useWallet((s) => s.balanceStatus);
  const connected = useWallet((s) => s.connected);
  const manual = useWallet((s) => s.manualRecipient);
  const session = useWallet((s) => s.session);
  const running = Object.values(procs).filter((p) => p.active);
  const addr = connected ?? manual;
  return (
    <div className="tray">
      <span className="tray-spacer" />
      {running.map((p) => (
        <button key={p.id} className="chip live" onClick={() => openApp(p.app)} title={`${p.name} is running. Open it.`}>
          <span className="dot" />
          <span className="hide-phone">{p.name}</span> {p.detail}
        </button>
      ))}
      {coord === 'offline' && (
        <button className="chip warn" onClick={() => openApp('network')} title="The coordinator is unreachable. Mining and AI access are temporarily unavailable.">
          <span className="dot" />
          offline
        </button>
      )}
      <button className="chip hide-phone" onClick={() => openApp('wallet')} title={balStatus === 'offline' ? 'Balance could not be refreshed' : 'Available credits'}>
        <AppIcon name="token" size={16} />
        {balance ? formatCredits(microToCredits(BigInt(balance.availableMicro))) : addr ? '…' : '0'} cr
      </button>
      <button className="chip" onClick={() => openApp('wallet')} title={session ? 'Signed in: can spend credits' : connected ? 'Wallet connected, not signed in' : manual ? 'Mining recipient only (cannot spend)' : 'No wallet'}>
        <AppIcon name="wallet" size={16} />
        {addr ? shortAddr(addr) : 'no wallet'}
        {session && <span className="dot" aria-label="signed in" />}
      </button>
      <button className="chip hide-phone token-chip" onClick={() => openApp('token')} title={config?.token ? 'Token details' : 'Token not launched yet'}>
        <AppIcon name="token" size={16} />
        {config?.token ? tokenLabel(config) : 'token: pre-launch'}
      </button>
      <Clock />
    </div>
  );
});

export function StartMenu({ onClose, onShutdown, closing }: { onClose: () => void; onShutdown: () => void; closing: boolean }) {
  const config = useCoordinator((s) => s.config);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('.sm-item')?.focus();
  }, []);
  const go = (id: AppId) => {
    onClose();
    openApp(id);
  };
  const onKey = (e: React.KeyboardEvent) => {
    const items = Array.from(ref.current?.querySelectorAll<HTMLElement>('.sm-item') ?? []);
    const i = items.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'ArrowDown') items[(i + 1) % items.length]?.focus();
    else if (e.key === 'ArrowUp') items[(i - 1 + items.length) % items.length]?.focus();
    else if (e.key === 'Home') items[0]?.focus();
    else if (e.key === 'End') items[items.length - 1]?.focus();
    else return;
    e.preventDefault();
  };
  return (
    <div ref={ref} id="start-menu" className={'start-menu ' + (closing ? 'leave' : 'enter')} role="menu" aria-label="Start" onKeyDown={onKey}>
      <div className="sm-side" aria-hidden>
        <Mark size={30} />
      </div>
      <div className="sm-list">
        {START_ORDER.map((id) => (
          <button key={id} data-start-app={id} className="sm-item" role="menuitem" onClick={() => go(id)}>
            <AppIcon name={APPS[id].icon} size={30} />
            <span className="sm-text">
              <b>{APPS[id].title}</b>
              <small>{APPS[id].blurb}</small>
            </span>
          </button>
        ))}
        <div className="sm-sep" />
        {config?.socialUrl && (
          <a className="sm-item" role="menuitem" href={config.socialUrl} target="_blank" rel="noopener noreferrer" onClick={onClose}>
            <AppIcon name="network" size={30} />
            <span className="sm-text">
              <b>{config.socialLabel || 'Community'}</b>
              <small>Follow NimoOS</small>
            </span>
          </a>
        )}
        <button className="sm-item" role="menuitem" onClick={onShutdown}>
          <AppIcon name="system" size={30} />
          <span className="sm-text">
            <b>Shut Down</b>
            <small>Stop local compute, close everything</small>
          </span>
        </button>
      </div>
    </div>
  );
}

export function Taskbar({ startOpen, onToggleStart, onSwitcher }: { startOpen: boolean; onToggleStart: () => void; onSwitcher: () => void }) {
  const count = useWm((s) => s.order.length);
  return (
    <footer className="taskbar">
      <button id="start-button" className="start-btn" aria-haspopup="menu" aria-expanded={startOpen} aria-controls="start-menu" onClick={onToggleStart}>
        <Mark size={24} />
        <span>nimu</span>
      </button>
      <button className="chip nimu-show-desktop" aria-label="Show desktop" onClick={()=>{for(const id of useWm.getState().order)useWm.getState().minimize(id);}}>Desktop</button>
      <button className="chip switcher-btn" data-switcher onClick={onSwitcher} aria-label="Switch apps">
        {count} open
      </button>
      <TaskButtons />
      <Tray />
    </footer>
  );
}
